import type { Field } from 'payload'

import deepMerge from '@/utilities/deepMerge'
import { ButtonProps } from '@/components/Button'

export type LinkAppearances =
  | 'default'
  | 'outline'
  | 'inline'
  | Exclude<ButtonProps['variant'], undefined | null>

export const appearanceOptions: Record<LinkAppearances, { label: string; value: string }> = {
  default: {
    label: 'Default',
    value: 'default',
  },
  outline: {
    label: 'Outline',
    value: 'outline',
  },
  inline: {
    label: 'Inline',
    value: 'inline',
  },
  black: {
    label: 'Black',
    value: 'black',
  },
  destructive: {
    label: 'Destructive',
    value: 'destructive',
  },
  ghost: {
    label: 'Ghost',
    value: 'ghost',
  },
  link: {
    label: 'Link',
    value: 'link',
  },
  secondary: {
    label: 'Secondary',
    value: 'secondary',
  },
  success: {
    label: 'Success',
    value: 'success',
  },
  danger: {
    label: 'Danger',
    value: 'danger',
  },
  warning: {
    label: 'Warning',
    value: 'warning',
  },
}

type LinkFieldFactory = (options?: {
  appearances?: LinkAppearances[] | false
  disableLabel?: boolean
  overrides?: Record<string, unknown>
  className?: string
}) => Field

type GetLinkUrlLinkParameter = {
  url?: string | null
  reference?: {
    value?:
      | string
      | number
      | null
      | {
          slug?: string | null
        }
  } | null
}

export function getLinkUrl(link: GetLinkUrlLinkParameter) {
  if (link.url) {
    return link.url
  }
  if (link.reference) {
    const slug =
      typeof link.reference?.value !== 'object'
        ? String(link.reference.value)
        : link.reference?.value?.slug
    if (slug === 'home') {
      return '/'
    } else if (slug) {
      return `/${slug}`
    }
  }
  return '/'
}

export const link: LinkFieldFactory = ({
  appearances,
  disableLabel = false,
  overrides = {},
} = {}) => {
  const linkResult: Field = {
    name: 'link',
    type: 'group',
    admin: {
      hideGutter: true,
    },
    fields: [],
  }

  // Add initial row with type and newTab
  linkResult.fields.push({
    type: 'row',
    fields: [
      {
        name: 'type',
        type: 'radio',
        admin: {
          layout: 'horizontal',
          width: '50%',
        },
        defaultValue: 'reference',
        options: [
          {
            label: 'Internal link',
            value: 'reference',
          },
          {
            label: 'Custom URL',
            value: 'custom',
          },
        ],
      },
      {
        name: 'newTab',
        type: 'checkbox',
        admin: {
          style: {
            alignSelf: 'flex-end',
          },
          width: '50%',
        },
        label: 'Open in new tab',
      },
    ],
  })

  const linkTypes: Field[] = [
    {
      name: 'reference',
      type: 'relationship',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'reference',
      },
      label: 'Document to link to',
      maxDepth: 1,
      relationTo: ['pages'],
      required: true,
    },
    {
      name: 'url',
      type: 'text',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'custom',
      },
      label: 'Custom URL',
      required: true,
    },
  ]

  if (!disableLabel) {
    linkResult.fields.push({
      type: 'row',
      fields: [
        ...linkTypes,
        {
          name: 'label',
          type: 'text',
          admin: {
            width: '50%',
          },
          label: 'Label',
          required: true,
        },
      ],
    })
  } else {
    linkResult.fields = [...linkResult.fields, ...linkTypes]
  }

  const appearanceOptionsToUse =
    appearances !== false
      ? Array.isArray(appearances)
        ? Object.values(appearanceOptions).filter((option) =>
            appearances.includes(option.value as LinkAppearances),
          )
        : Object.values(appearanceOptions)
      : []

  if (appearances !== false) {
    linkResult.fields.push({
      type: 'row',
      fields: [
        {
          name: 'appearance',
          type: 'select',
          admin: {
            description: 'Choose how the link should be rendered.',
            width: '50%',
          },
          defaultValue: 'default',
          options: appearanceOptionsToUse,
        },
        {
          name: 'textColor',
          type: 'select',
          admin: {
            condition: (_, siblingData) => siblingData?.appearance === 'inline',
            description: 'Text color for inline links',
            width: '50%',
          },
          defaultValue: 'ocean-blue',
          options: [
            {
              label: 'Ocean Blue (default)',
              value: 'ocean-blue',
            },
            {
              label: 'Black',
              value: 'black',
            },
            {
              label: 'Primary Pink',
              value: 'primary',
            },
          ],
        },
        {
          name: 'action',
          type: 'checkbox',
          admin: {
            width: '50%',
            style: {
              marginTop: '20px',
            },
          },
        },
        {
          name: 'disabled',
          type: 'checkbox',
          admin: {
            width: '50%',
            style: {
              marginTop: '20px',
            },
          },
          label: 'Disabled',
        },
        {
          name: 'className',
          type: 'text',
        },
      ],
    })
  }

  linkResult.fields.push({
    type: 'row',
    fields: [
      {
        name: 'icon',
        type: 'select',
        required: false,
        admin: {
          description: 'Optional icon to display with the link (used in navigation drawer)',
        },
        options: [
          // Navigation & Actions
          { label: 'Home', value: 'Home' },
          { label: 'Menu', value: 'Menu' },
          { label: 'X (Sluiten)', value: 'X' },
          { label: 'ArrowLeft', value: 'ArrowLeft' },
          { label: 'ArrowRight', value: 'ArrowRight' },
          { label: 'ArrowUp', value: 'ArrowUp' },
          { label: 'ArrowDown', value: 'ArrowDown' },
          { label: 'ChevronLeft', value: 'ChevronLeft' },
          { label: 'ChevronRight', value: 'ChevronRight' },
          { label: 'ChevronUp', value: 'ChevronUp' },
          { label: 'ChevronDown', value: 'ChevronDown' },
          { label: 'ChevronsLeft', value: 'ChevronsLeft' },
          { label: 'ChevronsRight', value: 'ChevronsRight' },
          { label: 'ChevronsUp', value: 'ChevronsUp' },
          { label: 'ChevronsDown', value: 'ChevronsDown' },
          { label: 'MoreHorizontal', value: 'MoreHorizontal' },
          { label: 'MoreVertical', value: 'MoreVertical' },

          // Users & People
          { label: 'User', value: 'User' },
          { label: 'Users', value: 'Users' },
          { label: 'Users2', value: 'Users2' },
          { label: 'UserPlus', value: 'UserPlus' },
          { label: 'UserMinus', value: 'UserMinus' },
          { label: 'UserCheck', value: 'UserCheck' },
          { label: 'UserX', value: 'UserX' },
          { label: 'UserCog', value: 'UserCog' },
          { label: 'UserCircle', value: 'UserCircle' },
          { label: 'UserSquare', value: 'UserSquare' },

          // Communication
          { label: 'Mail', value: 'Mail' },
          { label: 'MessageCircle', value: 'MessageCircle' },
          { label: 'MessageSquare', value: 'MessageSquare' },
          { label: 'Send', value: 'Send' },
          { label: 'Phone', value: 'Phone' },
          { label: 'Bell', value: 'Bell' },
          { label: 'BellOff', value: 'BellOff' },

          // Files & Documents
          { label: 'File', value: 'File' },
          { label: 'FileText', value: 'FileText' },
          { label: 'Files', value: 'Files' },
          { label: 'Folder', value: 'Folder' },
          { label: 'FolderOpen', value: 'FolderOpen' },
          { label: 'FilePlus', value: 'FilePlus' },
          { label: 'Download', value: 'Download' },
          { label: 'Upload', value: 'Upload' },

          // Media
          { label: 'Image', value: 'Image' },
          { label: 'Film', value: 'Film' },
          { label: 'Video', value: 'Video' },
          { label: 'Camera', value: 'Camera' },
          { label: 'Music', value: 'Music' },
          { label: 'Play', value: 'Play' },
          { label: 'Pause', value: 'Pause' },
          { label: 'Volume2', value: 'Volume2' },

          // Shopping & Commerce
          { label: 'ShoppingCart', value: 'ShoppingCart' },
          { label: 'ShoppingBag', value: 'ShoppingBag' },
          { label: 'CreditCard', value: 'CreditCard' },
          { label: 'Wallet', value: 'Wallet' },
          { label: 'DollarSign', value: 'DollarSign' },
          { label: 'Euro', value: 'Euro' },
          { label: 'Tag', value: 'Tag' },
          { label: 'Tags', value: 'Tags' },
          { label: 'Gift', value: 'Gift' },
          { label: 'Ticket', value: 'Ticket' },
          { label: 'BadgeCheck', value: 'BadgeCheck' },
          { label: 'BadgeDollarSign', value: 'BadgeDollarSign' },

          // Location & Map
          { label: 'MapPin', value: 'MapPin' },
          { label: 'Map', value: 'Map' },
          { label: 'Navigation', value: 'Navigation' },
          { label: 'Compass', value: 'Compass' },
          { label: 'Globe', value: 'Globe' },

          // Time & Calendar
          { label: 'Calendar', value: 'Calendar' },
          { label: 'Clock', value: 'Clock' },
          { label: 'Timer', value: 'Timer' },
          { label: 'AlarmClock', value: 'AlarmClock' },

          // Status & Alerts
          { label: 'Check', value: 'Check' },
          { label: 'CheckCircle', value: 'CheckCircle' },
          { label: 'CheckCircle2', value: 'CheckCircle2' },
          { label: 'AlertCircle', value: 'AlertCircle' },
          { label: 'AlertTriangle', value: 'AlertTriangle' },
          { label: 'Info', value: 'Info' },
          { label: 'XCircle', value: 'XCircle' },
          { label: 'HelpCircle', value: 'HelpCircle' },

          // Social & Sharing
          { label: 'Share', value: 'Share' },
          { label: 'Share2', value: 'Share2' },
          { label: 'Heart', value: 'Heart' },
          { label: 'HeartHandshake', value: 'HeartHandshake' },
          { label: 'HeartPulse', value: 'HeartPulse' },
          { label: 'HeartCrack', value: 'HeartCrack' },
          { label: 'HeartOff', value: 'HeartOff' },
          { label: 'HandHeart', value: 'HandHeart' },
          { label: 'Handshake', value: 'Handshake' },
          { label: 'Star', value: 'Star' },
          { label: 'ThumbsUp', value: 'ThumbsUp' },
          { label: 'ThumbsDown', value: 'ThumbsDown' },
          { label: 'Laugh', value: 'Laugh' },
          { label: 'PartyPopper', value: 'PartyPopper' },

          // Security
          { label: 'Lock', value: 'Lock' },
          { label: 'Unlock', value: 'Unlock' },
          { label: 'Key', value: 'Key' },
          { label: 'Shield', value: 'Shield' },
          { label: 'ShieldCheck', value: 'ShieldCheck' },
          { label: 'Eye', value: 'Eye' },
          { label: 'EyeOff', value: 'EyeOff' },

          // Links & External
          { label: 'Link', value: 'Link' },
          { label: 'Link2', value: 'Link2' },
          { label: 'ExternalLink', value: 'ExternalLink' },
          { label: 'Paperclip', value: 'Paperclip' },

          // Edit & Formatting
          { label: 'Edit', value: 'Edit' },
          { label: 'Edit2', value: 'Edit2' },
          { label: 'Edit3', value: 'Edit3' },
          { label: 'Pencil', value: 'Pencil' },
          { label: 'Trash', value: 'Trash' },
          { label: 'Trash2', value: 'Trash2' },
          { label: 'Copy', value: 'Copy' },
          { label: 'Scissors', value: 'Scissors' },

          // UI Controls
          { label: 'Settings', value: 'Settings' },
          { label: 'Sliders', value: 'Sliders' },
          { label: 'Filter', value: 'Filter' },
          { label: 'Search', value: 'Search' },
          { label: 'Maximize', value: 'Maximize' },
          { label: 'Minimize', value: 'Minimize' },
          { label: 'Plus', value: 'Plus' },
          { label: 'PlusCircle', value: 'PlusCircle' },
          { label: 'Minus', value: 'Minus' },
          { label: 'MinusCircle', value: 'MinusCircle' },

          // Buildings & Locations
          { label: 'Building', value: 'Building' },
          { label: 'Building2', value: 'Building2' },
          { label: 'Store', value: 'Store' },
          { label: 'Warehouse', value: 'Warehouse' },
          { label: 'Home', value: 'Home' },

          // Objects & Items
          { label: 'Book', value: 'Book' },
          { label: 'BookOpen', value: 'BookOpen' },
          { label: 'Bookmark', value: 'Bookmark' },
          { label: 'Award', value: 'Award' },
          { label: 'Trophy', value: 'Trophy' },
          { label: 'Target', value: 'Target' },
          { label: 'Flag', value: 'Flag' },
          { label: 'Briefcase', value: 'Briefcase' },
          { label: 'Package', value: 'Package' },

          // Learning & Education
          { label: 'GraduationCap', value: 'GraduationCap' },
          { label: 'BookMarked', value: 'BookMarked' },
          { label: 'Library', value: 'Library' },

          // Actions & Effects
          { label: 'Zap', value: 'Zap' },
          { label: 'Sparkles', value: 'Sparkles' },
          { label: 'Flame', value: 'Flame' },
          { label: 'Loader', value: 'Loader' },
          { label: 'RefreshCw', value: 'RefreshCw' },

          // Charts & Data
          { label: 'BarChart', value: 'BarChart' },
          { label: 'BarChart2', value: 'BarChart2' },
          { label: 'BarChart3', value: 'BarChart3' },
          { label: 'LineChart', value: 'LineChart' },
          { label: 'PieChart', value: 'PieChart' },
          { label: 'TrendingUp', value: 'TrendingUp' },
          { label: 'TrendingDown', value: 'TrendingDown' },

          // Layout & Structure
          { label: 'Layout', value: 'Layout' },
          { label: 'Grid', value: 'Grid' },
          { label: 'Columns', value: 'Columns' },
          { label: 'Rows', value: 'Rows' },
          { label: 'List', value: 'List' },
          { label: 'Layers', value: 'Layers' },

          // Weather & Nature
          { label: 'Sun', value: 'Sun' },
          { label: 'Moon', value: 'Moon' },
          { label: 'Cloud', value: 'Cloud' },
          { label: 'CloudRain', value: 'CloudRain' },
          { label: 'Leaf', value: 'Leaf' },
          { label: 'TreePine', value: 'TreePine' },

          // Shapes
          { label: 'Circle', value: 'Circle' },
          { label: 'Square', value: 'Square' },
          { label: 'Triangle', value: 'Triangle' },
          { label: 'Diamond', value: 'Diamond' },
          { label: 'Hexagon', value: 'Hexagon' },
          { label: 'Octagon', value: 'Octagon' },

          // Nature & Misc
          { label: 'Waves', value: 'Waves' },
          { label: 'Rainbow', value: 'Rainbow' },
          { label: 'Sparkle', value: 'Sparkle' },
          { label: 'Coffee', value: 'Coffee' },
          { label: 'Pizza', value: 'Pizza' },
          { label: 'Utensils', value: 'Utensils' },
          { label: 'Smile', value: 'Smile' },
          { label: 'Frown', value: 'Frown' },
          { label: 'Meh', value: 'Meh' },
          { label: 'Palette', value: 'Palette' },
          { label: 'Paintbrush', value: 'Paintbrush' },
        ],
      },
      {
        type: 'ui',
        name: 'iconPreview',
        admin: {
          width: '2rem',
          components: {
            Field: '@/fields/IconPreview',
          },
        },
      }
    ]
  })

  return deepMerge(linkResult, overrides)
}
